import { WarningOutlined } from '@ant-design/icons';
import { usePostFrontRichiesteIdCopia } from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { getNetworkErrorMessage } from '@framework/auth/http/errors/getNetworkErrorMessage.ts';
import { useHistory } from '@framework/ui/core/router/TypedRoute.tsx';
import { Button, message, theme } from 'antd';
import modal from 'antd/es/modal';
import { type ButtonProps } from 'antd/lib';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { redirectToSubmissionPage } from './principal-module-redirector/redirectToSubmissionPage.tsx';

const { useToken } = theme;

export interface ISubmissionCloneButtonProps extends ButtonProps {
  submissionId?: string | null;
  isJustCreated?: boolean;
  iconOnlyButton?: boolean;
}

/**
 * Pulsante per duplicare una richiesta lato FO
 */
export function SubmissionCloneButton(props: ISubmissionCloneButtonProps) {
  const { submissionId, iconOnlyButton, isJustCreated, ...otherProps } = props;
  const { token } = useToken();
  const history = useHistory();
  const clone = usePostFrontRichiesteIdCopia();
  const { t } = useTranslation();

  const handleClone = useCallback(() => {
    if (!submissionId) {
      // biome-ignore format: <tbd>
      message.error(t("submission-clone-button.la-richiesta-deve-essere-salvata-per-duplicare", `La richiesta deve essere salvata per poter essere duplicata.`));
      return;
    }

    modal.confirm({
      title: t(
        'submission-clone-button.sei-sicuro-di-voler-duplicare-la-richiesta',
        'Sei sicuro di voler duplicare la richiesta?',
      ),
      content: t(
        'submission-clone-button.la-richiesta-verra-duplicata-senza-i-file-allegati',
        'La richiesta verrà duplicata senza i file allegati.',
      ),
      icon: <WarningOutlined style={{ color: token.colorWarning }} />,
      type: 'error',
      okText: t('submission-clone-button.duplica', 'Duplica'),
      cancelText: t('submission-clone-button.chiudi', 'Chiudi'),
      async onOk() {
        try {
          const clonedSubmissionId = await clone.mutateAsync({
            id: submissionId,
          });

          message.success(
            t(
              'submission-clone-button.richiesta-duplicata-con-successo',
              'Richiesta duplicata con successo.',
            ),
          );

          await redirectToSubmissionPage(clonedSubmissionId, history);
        } catch (e) {
          console.error(
            t(
              'submission-clone-button.errore-nel-duplicare-la-richiesta',
              `Errore nel duplicare la richiesta`,
            ),
            { e },
          );
          // biome-ignore format: <tbd>
          message.error(getNetworkErrorMessage(e, t("submission-clone-button.errore-durante-la-duplicazione-della-richiesta",`Si è verificato un errore durante la duplicazione della richiesta.`)));
        }
      },
      onCancel() {},
    });
  }, [token.colorWarning, clone, submissionId, history]);

  return (
    <>
      <Button
        type="default"
        size="large"
        disabled={isJustCreated}
        onClick={() => handleClone()}
        {...otherProps}
      >
        {!iconOnlyButton && (
          <>
            {t(
              'submission-clone-button.duplica-richiesta',
              'Duplica la Richiesta',
            )}
          </>
        )}
      </Button>
    </>
  );
}
